import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface UseAdminCrudOptions<T, TForm> {
  table: string;
  select?: string;
  orderBy?: { column: string; ascending?: boolean };
  entityName: string;
  getInitialFormData: () => TForm;
  mapItemToForm: (item: T) => TForm;
  buildPayload: (formData: TForm) => Record<string, unknown>;
  validatePayload?: (formData: TForm) => string | null;
}

interface UseAdminCrudReturn<T, TForm> {
  // Data state
  items: T[];
  loading: boolean;

  // Editor state
  editorOpen: boolean;
  setEditorOpen: (open: boolean) => void;
  editingItem: T | null;
  formData: TForm;
  setFormData: React.Dispatch<React.SetStateAction<TForm>>;
  isSaving: boolean;

  // Delete state
  deleteDialogOpen: boolean;
  setDeleteDialogOpen: (open: boolean) => void;
  itemToDelete: T | null;
  isDeleting: boolean;

  // Actions
  fetchData: () => Promise<void>;
  openCreateEditor: () => void;
  openEditEditor: (item: T) => void;
  handleSave: () => Promise<void>;
  confirmDelete: (item: T) => void;
  handleDelete: () => Promise<void>;
}

export function useAdminCrud<T extends { id: string }, TForm>(
  options: UseAdminCrudOptions<T, TForm>
): UseAdminCrudReturn<T, TForm> {
  const { toast } = useToast();
  const {
    table,
    select = '*',
    orderBy,
    entityName,
    getInitialFormData,
    mapItemToForm,
    buildPayload,
    validatePayload,
  } = options;

  // Data state
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const [formData, setFormData] = useState<TForm>(getInitialFormData());
  const [isSaving, setIsSaving] = useState(false);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<T | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    
    // Use type assertion to work with dynamic table names
    const query = orderBy
      ? supabase
          .from(table as 'categories')
          .select(select)
          .order(orderBy.column as 'display_order', { ascending: orderBy.ascending ?? true })
      : supabase.from(table as 'categories').select(select);

    const { data, error } = await query;

    if (error) {
      toast({
        title: `Error fetching ${entityName}s`,
        description: error.message,
        variant: 'destructive',
      });
    } else if (data) {
      setItems(data as unknown as T[]);
    }
    setLoading(false);
  }, [table, select, orderBy?.column, orderBy?.ascending, entityName, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = useCallback(() => {
    setFormData(getInitialFormData());
    setEditingItem(null);
  }, [getInitialFormData]);

  const openCreateEditor = useCallback(() => {
    resetForm();
    setEditorOpen(true);
  }, [resetForm]);

  const openEditEditor = useCallback(
    (item: T) => {
      setEditingItem(item);
      setFormData(mapItemToForm(item));
      setEditorOpen(true);
    },
    [mapItemToForm]
  );

  const handleSave = useCallback(async () => {
    if (validatePayload) {
      const error = validatePayload(formData);
      if (error) {
        toast({ title: error, variant: 'destructive' });
        return;
      }
    }

    setIsSaving(true);
    const payload = buildPayload(formData);

    if (editingItem) {
      const { error } = await supabase
        .from(table as 'categories')
        .update(payload as never)
        .eq('id' as never, editingItem.id as never);

      if (error) {
        toast({
          title: `Error updating ${entityName}`,
          description: error.message,
          variant: 'destructive',
        });
      } else {
        toast({ title: `${capitalize(entityName)} updated` });
        setEditorOpen(false);
        fetchData();
      }
    } else {
      const { error } = await supabase
        .from(table as 'categories')
        .insert(payload as never);

      if (error) {
        toast({
          title: `Error creating ${entityName}`,
          description: error.message,
          variant: 'destructive',
        });
      } else {
        toast({ title: `${capitalize(entityName)} created` });
        setEditorOpen(false);
        fetchData();
      }
    }
    setIsSaving(false);
  }, [formData, editingItem, table, entityName, buildPayload, validatePayload, toast, fetchData]);

  const confirmDelete = useCallback((item: T) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!itemToDelete) return;

    setIsDeleting(true);
    const { error } = await supabase
      .from(table as 'categories')
      .delete()
      .eq('id' as never, itemToDelete.id as never);

    if (error) {
      toast({
        title: `Error deleting ${entityName}`,
        description: error.message,
        variant: 'destructive',
      });
    } else {
      toast({ title: `${capitalize(entityName)} deleted` });
      fetchData();
    }

    setIsDeleting(false);
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  }, [itemToDelete, table, entityName, toast, fetchData]);

  return {
    items,
    loading,
    editorOpen,
    setEditorOpen,
    editingItem,
    formData,
    setFormData,
    isSaving,
    deleteDialogOpen,
    setDeleteDialogOpen,
    itemToDelete,
    isDeleting,
    fetchData,
    openCreateEditor,
    openEditEditor,
    handleSave,
    confirmDelete,
    handleDelete,
  };
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
